import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import './styles/blogOverrides.css';
import App from './App';
import logoSymbol from './assets/icons/logo-symbol.svg';
import reportWebVitals from './reportWebVitals';

const rootElement = document.getElementById('root') as HTMLElement;
const app = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// Use hydration if content was prerendered by react-snap.
// react-snap prerenders with a fixed viewport and no auth state, so minor
// mismatches with the client render are expected (useMediaQuery, auth UI, etc.).
// React recovers automatically by client-rendering the mismatched subtree.
const mountApp = () => {
  if (rootElement.hasChildNodes()) {
    ReactDOM.hydrateRoot(rootElement, app, {
      onRecoverableError(error) {
        // Silence expected react-snap hydration mismatches in production
        if (process.env.NODE_ENV === 'development') {
          console.error(error);
        }
      },
    });
  } else {
    ReactDOM.createRoot(rootElement).render(app);
  }
};

// Lazy-hydrate the prerendered homepage on first user interaction to cut mobile
// TBT / main-thread work on initial load. The prerendered HTML is fully styled
// and readable immediately; React hydrates as soon as the visitor shows intent.
// A click arriving before hydration is captured, deferred and replayed so no CTA
// (e.g. "Vyzkouset zdarma") interaction is ever lost. Homepage-only; every other
// route hydrates immediately as before.
const isLazyHydratable =
  typeof window !== 'undefined' &&
  typeof AbortController !== 'undefined' &&
  rootElement.hasChildNodes() &&
  window.location.pathname === '/' &&
  !/ReactSnap/i.test(navigator.userAgent);

if (isLazyHydratable) {
  let hydrated = false;
  let pendingClickTarget: Element | null = null;
  const abortController = new AbortController();
  const interactionEvents = ['pointerdown', 'touchstart', 'mousedown', 'keydown', 'scroll', 'mousemove', 'focusin'];

  const hydrateNow = () => {
    if (hydrated) return;
    hydrated = true;
    abortController.abort();
    mountApp();
    if (pendingClickTarget) {
      const el = pendingClickTarget as HTMLElement;
      pendingClickTarget = null;
      // Replay the original click once React has attached its handlers.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          try {
            el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
          } catch (err) {
            /* noop */
          }
        })
      );
    }
  };

  const onClickCapture = (e: MouseEvent) => {
    if (hydrated) return;
    const target = e.target as Element | null;
    const actionable = target && target.closest ? target.closest('a,button,[role="button"]') : null;
    if (actionable) {
      // Block the dead pre-hydration click, remember it, hydrate, then replay it.
      e.preventDefault();
      e.stopPropagation();
      pendingClickTarget = actionable;
    }
    hydrateNow();
  };

  document.addEventListener('click', onClickCapture, { capture: true, signal: abortController.signal });
  interactionEvents.forEach((ev) =>
    window.addEventListener(ev, hydrateNow, { capture: true, passive: true, signal: abortController.signal })
  );
} else {
  mountApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Temporary client-side hide of the HomePage story button
try {
  // Ensure favicon uses project logo (works even if server default points elsewhere)
  const ensureFavicon = () => {
    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    // Create/update SVG favicon
    const createLink = (rel: string, href: string, type?: string, sizes?: string) => {
      const link: HTMLLinkElement = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      if (type) link.setAttribute('type', type);
      if (sizes) link.setAttribute('sizes', sizes);
      head.appendChild(link);
    };

    // Remove existing rel=icon to avoid conflicts
    const existingIcons = head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    existingIcons.forEach((n) => n.parentElement?.removeChild(n));

    // Add SVG favicon (preferred)
    createLink('icon', logoSymbol, 'image/svg+xml', 'any');
    // Keep a shortcut icon fallback pointing to default PNG for older browsers
    createLink('shortcut icon', '/logo192.png', 'image/png', '192x192');
  };

  // Run immediately and after initial render
  ensureFavicon();

  const hideStoryCta = () => {
    if (window.location && (window.location.pathname === '/' || window.location.pathname === '/home')) {
      const btns = document.querySelectorAll('button');
      for (const b of Array.from(btns)) {
        const t = (b.textContent || '').trim();
        if (t === 'Příběh vzniku právního bota') {
          (b as HTMLElement).style.display = 'none';
          return true;
        }
      }
    }
    return false;
  };

  // Run immediately and also observe for React renders
  if (!hideStoryCta()) {
    const mo = new MutationObserver(() => {
      if (hideStoryCta()) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    // Fallback timeout disconnect
    setTimeout(() => mo.disconnect(), 5000);
  }
} catch {}

// Add small red TOP badge next to "Analýza smluv" in navigation
try {
  const injectTopBadge = () => {
    let injected = false;

    const makeBadge = () => {
      const b = document.createElement('span');
      b.textContent = 'TOP';
      Object.assign(b.style, {
        marginLeft: '6px',
        padding: '0 6px',
        height: '18px',
        lineHeight: '18px',
        borderRadius: '10px',
        backgroundColor: '#e72f0d',
        color: '#fff',
        fontSize: '10px',
        fontWeight: '700',
        letterSpacing: '0.4px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      } as CSSStyleDeclaration);
      b.setAttribute('data-top-badge', '1');
      return b;
    };

    // Prefer header navigation container
    const headerNav = document.getElementById('primary-navigation');
    const scopeSelectors = headerNav ? ['#primary-navigation [role="link"]'] : ['[role="link"]'];

    for (const sel of scopeSelectors) {
      const linkNodes = Array.from(document.querySelectorAll(sel)).filter((n) => /Analýza\s+smluv/i.test((n.textContent || '').trim()));
      for (const link of linkNodes) {
        const style = window.getComputedStyle(link as HTMLElement);
        if ((link as HTMLElement).offsetParent === null || style.display === 'none' || style.visibility === 'hidden') continue;
        const labels = Array.from(link.querySelectorAll('p, span, div')).filter((n) => /Analýza\s+smluv/i.test((n.textContent || '').trim()));
        const labelEl = labels[labels.length - 1] || link;
        if (labelEl && !labelEl.querySelector('[data-top-badge="1"]')) {
          labelEl.appendChild(makeBadge());
          injected = true;
        }
      }
      if (injected) break;
    }

    return injected;
  };

  if (!injectTopBadge()) {
    const mo2 = new MutationObserver(() => {
      if (injectTopBadge()) mo2.disconnect();
    });
    mo2.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => mo2.disconnect(), 6000);
  }
} catch {}
